import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import type { PropsWithChildren } from 'react'
import React from 'react'
import clsx from 'clsx'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { AccommodationIndividualRoomTileImageContainer } from './AccommodationIndividualRoomTileImage'
import { AccommodationIndividualRoomTileDetailsContainer } from './AccommodationIndividualRoomTileDetails'
import { AccommodationIndividualRoomTilePricingContainer } from './AccommodationIndividualRoomTilePricing'
import { AccommodationIndividualRoomTileHeaderContainer } from './AccommodationIndividualRoomTileHeader'
import { AccommodationIndividualRoomTileGridBase } from './AccommodationIndividualRoomRoomTileGridBase'

const AccommodationIndividualRoomTileGrid = styled(AccommodationIndividualRoomTileGridBase)`
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(8)} ${rem(20)};

  &.hasImage {
    grid-template:
      "header image" auto
      "details details" auto
      "pricing pricing" auto / 1fr auto;

    ${mediaQueryUp.desktop} {
      grid-template:
        "image header pricing" auto
        "image details pricing" auto;

      &.image-size-medium {
        grid-template-columns: ${rem(400)} 1fr ${rem(250)};
      }
      &.image-size-small {
        grid-template-columns: ${rem(300)} 1fr ${rem(250)};
      }
    }
  }

  &:not(.hasImage) {
    grid-template:
      "header" auto
      "details" auto
      "pricing" auto;

    ${mediaQueryUp.desktop} {
      grid-template:
        "header pricing" auto
        "details pricing" auto / 1fr auto;
    }

    > ${AccommodationIndividualRoomTileImageContainer} {
      display: none;
    }
  }

  > ${AccommodationIndividualRoomTileImageContainer} {
    grid-area: image;

    /* Make image go to the edge of the tile */
    ${mediaQueryUp.desktop} {
      margin-top: calc(${rem(-20)} - 1px);
      margin-bottom: calc(${rem(-20)} - 1px);
      margin-left: calc(${rem(-20)} - 1px);
    }
  }

  > ${AccommodationIndividualRoomTileHeaderContainer} {
    grid-area: header;
  }

  > ${AccommodationIndividualRoomTileDetailsContainer} {
    grid-area: details;
  }

  > ${AccommodationIndividualRoomTilePricingContainer} {
    grid-area: pricing;
  }
`

interface Props extends PropsWithChildren {
  roomPackage?: App.HotelPackage | App.BedbankPackage
  imageSize?: 'small' | 'medium'
}

function AccommodationIndividualRoomTile(props: Props) {
  const { roomPackage, imageSize = 'small', children } = props
  const room = roomPackage ? (isHotelPackage(roomPackage) ? roomPackage.roomType : roomPackage) : null

  return <AccommodationIndividualRoomTileGrid
    className={clsx(
      `image-size-${imageSize}`,
      {
        hasImage: !!room?.images?.[0]?.id,
      })}
    background="neutral-eight"
    tabletBorder="neutral-five"
  >
    {children}
  </AccommodationIndividualRoomTileGrid>
}

export default AccommodationIndividualRoomTile
